import { Button, Modal as BootstrapModal } from 'react-bootstrap';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ListFilter from '../ListFilter';
import BUILD_TIME_CONFIG_TYPES from '../../../../../config/buildTimeConfigTypes';
import CreateLayoutItemModal from './CreateLayoutItemModal';
import PubSub from '../../../../../services/PubSub';
import { useUIConfig } from '../Context';
import { useHistory } from 'react-router-dom';

const AddItemInSortableObjectModal = ({
    show,
    keyName,
    onAdd,
    onCancel,
    allowedGroups,
    showBlocks = true,
    showComponents = true,
}) => {
    const [translate] = useTranslation();
    const { add, components, blocks, applets, navigateToLayout } = useUIConfig();
    const history = useHistory();

    const [selectingLayoutItemType, setSelectingLayoutItemType] = useState(showBlocks ? BUILD_TIME_CONFIG_TYPES.BLOCKS : BUILD_TIME_CONFIG_TYPES.COMPONENTS);
    const [layoutItem, setLayoutItem] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);

    useEffect(() => {
        if (show) {
            setLayoutItem('');
        }
    }, [show]);

    const addLayoutItem = useCallback(({ layoutItemName, layoutItem, fromExisting = false }) => {
        if (applets[layoutItemName] || blocks[layoutItemName] || components[layoutItemName]) {
            PubSub.publish("show_info", translate("OTM_LAYOUT_ITEM_ALREADY_DEFINED"));
            return;
        }

        if (fromExisting) {
            switch (selectingLayoutItemType) {
                case BUILD_TIME_CONFIG_TYPES.BLOCKS: {
                    layoutItem = blocks[layoutItem];
                    break;
                }
                case BUILD_TIME_CONFIG_TYPES.COMPONENTS:
                default: {
                    layoutItem = components[layoutItem];
                }
            }
        }
        add([selectingLayoutItemType, layoutItemName], layoutItem, true);
        setShowCreateModal(false);

        navigateToLayout({ history, layoutType: selectingLayoutItemType, layoutName: layoutItemName, state: { keepBcContext: true } });
    }, [selectingLayoutItemType, add, components, blocks, applets, history, navigateToLayout, translate]);

    const listDisplayButtons = useMemo(() => {
        return (
            <div className="mb-2">
                <label className="text-secondary h6 me-2">{translate("ITEM_TYPE")}</label>
                {showComponents ? <Button
                    onClick={() => {
                        if (selectingLayoutItemType !== BUILD_TIME_CONFIG_TYPES.COMPONENTS) {
                            setSelectingLayoutItemType(BUILD_TIME_CONFIG_TYPES.COMPONENTS);
                            setLayoutItem('');
                        }
                    }}
                    variant={selectingLayoutItemType === BUILD_TIME_CONFIG_TYPES.COMPONENTS ? 'dark' : 'primary'}
                    size="sm"
                    className='me-2'
                >
                    {translate('OTM_COMPONENT')}
                </Button> : null}
                {showBlocks ? <Button
                    onClick={() => {
                        if (selectingLayoutItemType !== BUILD_TIME_CONFIG_TYPES.BLOCKS) {
                            setSelectingLayoutItemType(BUILD_TIME_CONFIG_TYPES.BLOCKS);
                            setLayoutItem('');
                        }
                    }}
                    variant={selectingLayoutItemType === BUILD_TIME_CONFIG_TYPES.BLOCKS ? 'dark' : 'primary'}
                    size="sm"
                    className='me-2'
                >
                    {translate('OTM_BLOCK')}
                </Button> : null}
            </div>
        );
    }, [showComponents, showBlocks, selectingLayoutItemType, translate]);

    const layoutItemList = useMemo(() => {
        return (
            <div className="h-100 pe-2" style={{ overflowY: 'auto' }}>
                <ListFilter
                    type={selectingLayoutItemType}
                    allowedGroups={allowedGroups}
                    isTopLevel={false}
                    createNew={false}
                    onSelect={(layoutItem) => setLayoutItem(layoutItem)}
                />
            </div>
        );
    }, [selectingLayoutItemType, allowedGroups]);

    const renderModal = useMemo(() => {
        return (
            <BootstrapModal size='lg' show={show} backdrop='static'>
                <BootstrapModal.Header className='h5 text-secondary'>
                    {translate("OTM_ADD_ITEM_TO")} {keyName} ({translate("ITEM_TYPE")} {selectingLayoutItemType})
                    <Button
                        onClick={() => { setShowCreateModal(true) }}
                        varianta="primary"
                        size="sm"
                    >
                        {translate("OTM_CREATE_NEW_ITEM")}
                    </Button>
                </BootstrapModal.Header>
                <BootstrapModal.Body className="overflow-hidden d-flex flex-column" style={{ height: 'calc(100vh / 1.35)' }}>
                    {listDisplayButtons}
                    {layoutItemList}
                </BootstrapModal.Body>
                <BootstrapModal.Footer>
                    <Button onClick={() => onAdd({ layoutItem: null })} className='btn-sm'>{translate('OTM_ADD_WITHOUT_ITEM')}</Button>
                    <Button disabled={layoutItem === ''} onClick={() => onAdd({ layoutItem })} className='btn-sm'>{translate('OTM_ADD')}</Button>
                    <Button onClick={onCancel} variant='secondary' className='btn-sm'>{translate('OTM_CANCEL')}</Button>
                </BootstrapModal.Footer>
            </BootstrapModal>
        );
    }, [onAdd, onCancel, show, keyName, layoutItem, selectingLayoutItemType, listDisplayButtons, layoutItemList, translate]);

    return (
        <>
            {renderModal}
            <CreateLayoutItemModal
                layoutItemType={selectingLayoutItemType}
                show={showCreateModal}
                onCreate={({ layoutItemName, layoutItem, fromExisting }) => { addLayoutItem({ layoutItemName, layoutItem, fromExisting }); onAdd({ layoutItem: layoutItemName }) }}
                onCancel={() => setShowCreateModal(false)}
            />
        </>
    );
};

export default AddItemInSortableObjectModal;